import { z } from 'zod';
import { AssetRef, CtaPayload, FeaturePayload, GalleryItem, GalleryLayout, RichText, Uuid, } from './blocks.js';
import { JournalArticleRoute, SiteAccess, SeoProjection, ShootRoute, Slug } from './content.js';
const sectionIdentity = {
    id: Uuid,
    position: z.number().int().nonnegative(),
};
export const AdminShootSection = z.discriminatedUnion('type', [
    z
        .object({
        ...sectionIdentity,
        type: z.literal('EditorialText'),
        payload: z.object({ body: RichText }).strict(),
    })
        .strict(),
    z
        .object({
        ...sectionIdentity,
        type: z.literal('Gallery'),
        payload: z.object({ layout: GalleryLayout, items: z.array(GalleryItem).min(1) }).strict(),
    })
        .strict(),
    z
        .object({
        ...sectionIdentity,
        type: z.literal('Feature'),
        payload: FeaturePayload,
    })
        .strict(),
]);
export const ShootEditorPayload = z
    .object({
    title: z.string().trim().min(1),
    subtitle: z.string().trim().min(1).optional(),
    description: RichText.optional(),
    category: z
        .string()
        .trim()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .optional(),
    dateLabel: z.string().trim().min(1).optional(),
    cover: AssetRef,
    seo: SeoProjection,
    cta: CtaPayload,
})
    .strict();
export const VersionedShoot = z
    .object({
    id: Uuid,
    slug: Slug,
    route: ShootRoute,
    siteAccess: SiteAccess,
    revision: z.number().int().nonnegative(),
    payload: ShootEditorPayload,
    sections: z.array(AdminShootSection),
})
    .strict();
export const ShootSummary = VersionedShoot.omit({ sections: true })
    .extend({
    position: z.number().int().nonnegative(),
    photoCount: z.number().int().nonnegative(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const CreateShootRequest = VersionedShoot.pick({
    slug: true,
    siteAccess: true,
    payload: true,
    sections: true,
}).strict();
export const UpdateShootRequest = CreateShootRequest.extend({
    expectedRevision: z.number().int().nonnegative(),
}).strict();
export const UpdateShootSiteAccessRequest = z
    .object({
    siteAccess: SiteAccess,
    expectedRevision: z.number().int().nonnegative(),
})
    .strict();
export const UpdateShootOrderRequest = z.object({ shootIds: z.array(Uuid).min(1) }).strict();
export const DeleteShootRequest = z
    .object({ expectedRevision: z.number().int().nonnegative() })
    .strict();
export const DeleteShootResponse = z
    .object({
    deleted: z.literal(true),
    id: Uuid,
    deletedAssets: z.number().int().nonnegative(),
})
    .strict();
export const AdminJournalSection = z.discriminatedUnion('type', [
    z
        .object({
        ...sectionIdentity,
        type: z.literal('RichText'),
        payload: z.object({ body: RichText }).strict(),
    })
        .strict(),
    z
        .object({
        ...sectionIdentity,
        type: z.literal('Image'),
        payload: z.object({ media: AssetRef, caption: z.string().trim().min(1).optional() }).strict(),
    })
        .strict(),
    z
        .object({
        ...sectionIdentity,
        type: z.literal('Gallery'),
        payload: z.object({ layout: GalleryLayout, items: z.array(GalleryItem).min(1) }).strict(),
    })
        .strict(),
]);
export const JournalEditorPayload = z
    .object({
    title: z.string().trim().min(1),
    excerpt: z.string().trim().min(1),
    category: z.string().trim().min(1),
    dateLabel: z.string().trim().min(1),
    publishedAt: z.iso.datetime().optional(),
    cover: AssetRef,
    seo: SeoProjection,
})
    .strict();
export const VersionedJournalPost = z
    .object({
    id: Uuid,
    slug: Slug,
    route: JournalArticleRoute,
    siteAccess: SiteAccess,
    revision: z.number().int().nonnegative(),
    payload: JournalEditorPayload,
    sections: z.array(AdminJournalSection),
})
    .strict();
export const JournalPostSummary = VersionedJournalPost.omit({ sections: true }).strict();
export const CreateJournalPostRequest = VersionedJournalPost.pick({
    slug: true,
    siteAccess: true,
    payload: true,
    sections: true,
}).strict();
export const UpdateJournalPostRequest = CreateJournalPostRequest.extend({
    expectedRevision: z.number().int().nonnegative(),
}).strict();
export const ShootsResponse = z.object({ shoots: z.array(ShootSummary) }).strict();
export const ShootResponse = z.object({ shoot: VersionedShoot }).strict();
export const JournalPostsResponse = z.object({ journal: z.array(JournalPostSummary) }).strict();
export const JournalPostResponse = z.object({ journal: VersionedJournalPost }).strict();
export const EditorialRevisionConflictEnvelope = z
    .object({
    error: z
        .object({
        code: z.literal('REVISION_CONFLICT'),
        message: z.literal('The content changed since it was loaded'),
        expectedRevision: z.number().int().nonnegative(),
        currentRevision: z.number().int().nonnegative(),
        current: z.union([VersionedShoot, VersionedJournalPost]),
        submitted: z.union([
            UpdateShootRequest,
            UpdateShootSiteAccessRequest,
            DeleteShootRequest,
            UpdateJournalPostRequest,
        ]),
    })
        .strict(),
})
    .strict();
